'use client';

import React, { FC, useEffect, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Plan } from '@/data/Plans';

import { useTranslations } from 'next-intl';

import {Link} from "@/i18n/routing";
import UTMCampaignBuilderUtils from '@/utils/UTMCampaignBuilderUtils';

interface PricingPlanSectionProps {
	subscriptionPlans: Plan[];
	className: string;
}
const PricingPlanSection: FC<PricingPlanSectionProps> = ({ subscriptionPlans, className }) => {
	const [isAnnual, setIsAnnual] = useState(true);
	const t = useTranslations('page.pricing');

	useEffect(() => {
		const priceElements = document.querySelectorAll('.plan-price');
		priceElements.forEach((item) => {
			const monthlyPrice = item.getAttribute('data-monthly-price') ?? '';
			const annualPrice = item.getAttribute('data-annual-price') ?? '';
			if (item.innerHTML) {
				item.innerHTML = isAnnual ? annualPrice : monthlyPrice;
			}
		});
	}, [isAnnual]);

	const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsAnnual(event.target.checked);
	};

	return (
		<>
			<div className={className}>
				<div className="inner-container-small text-center position-relative mb-8">
					<form className="d-flex align-items-center justify-content-center mb-4 mb-xl-0">
						<span className="fw-semibold text-white">{t('monthly')}</span>

						{/* Switch */}
						<div className="form-check form-switch form-check-lg mx-2 mb-0">
							<input className="form-check-input mt-0 price-toggle" type="checkbox" id="flexSwitchCheckDefault" checked={isAnnual} onChange={handleToggleChange} />
						</div>

						<div className="position-relative">
							<span className="fw-semibold text-white">{t('yearly')}</span>
							<span className="badge bg-danger position-absolute top-0 start-100 translate-middle mt-n2 ms-2 ms-md-5">{t('save', { percent: '20' })}</span>
						</div>
					</form>
				</div>
			</div>

			<div className="container mt-n9">
				<Swiper
					modules={[Pagination]}
					spaceBetween={30}
					slidesPerView={1}
					breakpoints={{
						576: { slidesPerView: 1 },
						768: { slidesPerView: 2 },
						992: { slidesPerView: 3 },
					}}
					pagination={{
						clickable: true,
						el: '#pager2',
					}}
					loop={false}
				>
					<span slot="container-start">
						<div className="pt-3"></div>
					</span>

					{subscriptionPlans.map((plan) => (
						<SwiperSlide className="swiper-slide" key={plan.id}>
							<div className={plan.featured ? 'card border border-primary p-0' : 'card shadow border overflow-hidden p-0'}>
								{plan.featured && <div className="bg-primary text-white rounded position-absolute top-0 start-50 translate-middle px-2 py-1">{t('subscriptions.featured')}</div>}
								<div className={'card-header border-bottom p-4' + (plan.featured && ' bg-primary bg-opacity-10 border-primary border-opacity-25')}>
									<h6 className="mb-1">{plan.label}</h6>
									<span className="mb-3 text-muted">{plan.description}</span>
									<p className="mb-3">
										<span className="h2 mb-0 plan-price" data-monthly-price={plan.price.monthly} data-annual-price={plan.price.annual}>
											{isAnnual ? plan.price.annual : plan.price.monthly}
										</span>
										<span className="ps-1 text-muted">{plan.duration}</span>
									</p>

									<Link href={UTMCampaignBuilderUtils.generate(process.env.NEXT_PUBLIC_APP_URL + '/v/register', 'www.medentica.app', 'website', 'website-pricing', plan.id)} target="_blank" className={'btn w-100 ' + (plan.featured ? 'btn-primary' : 'btn-dark')}>
										{t('subscriptions.get_started')}
									</Link>
								</div>

								<div className={'card-body p-4' + (plan.featured && ' bg-primary bg-opacity-10')}>
									<div className="text-center text-lg-start">
										<ul className="list-group list-group-borderless mb-2 mb-sm-4">
											{plan.features.map((feature, index) => (
												<li className="list-group-item d-flex heading-color mb-0" key={index}>
													{feature}
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						</SwiperSlide>
					))}

					<span slot="container-end">
						<div id="pager2" className="swiper-pagination swiper-pagination-primary position-relative mt-4" />
					</span>
				</Swiper>
			</div>
		</>
	);
};

export default PricingPlanSection;
